import { useAPIQuery } from "@dev2_techwalnut/tw-api-query-hook";
import { Table } from "@dev2_techwalnut/tw-react-crud-dashlite";
import React, { useMemo, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";

const unwrapLookups = (payload) => {
    const nested = payload?.results?.data || payload?.data || payload;
    return {
        departments: Array.isArray(nested?.departments)
            ? nested.departments
            : Array.isArray(payload?.departments)
            ? payload.departments
            : [],
    };
};

const columns = [
    {
        accessor: "machine_name",
        header: "Machine",
        sortable: true,
        cell: (row) => (
            <NavLink
                to="/onsite-issue-list"
                state={{ machineId: row.id, machineName: row.machine_name }}
            >
                {row.machine_name}
            </NavLink>
        ),
    },
    {
        accessor: "department_name",
        header: "Department",
        sortable: true,
        cell: (row) => row.department_name || "—",
    },
    {
        accessor: "pending_issue_count",
        header: "Pending issues",
        sortable: true,
    },
    {
        accessor: "open_issue_count",
        header: "Open issues",
        sortable: true,
    },
    {
        accessor: "total_issue_count",
        header: "Total issues",
        sortable: true,
    },
];

const OnsiteIssueMachines = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialEntries = Number(searchParams.get("entries")) || 10;
    const initialPage = Number(searchParams.get("page")) || 1;
    const [entries, setEntries] = useState(initialEntries);
    const [pageIndex, setPageIndex] = useState(initialPage - 1);
    const [globalFilterValue, setGlobalFilterValue] = useState(
        searchParams.get("q") || ""
    );
    const [departmentId, setDepartmentId] = useState(
        searchParams.get("department_id") || ""
    );
    const [sorting, setSorting] = useState([]);

    const { useFetch: useFetchLookups } = useAPIQuery({
        key: "api/onsite-issues/lookups",
        endpoint: "/api/onsite-issues/lookups/",
    });
    const { data: lookupResponse } = useFetchLookups({
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });
    const departments = useMemo(
        () => unwrapLookups(lookupResponse).departments,
        [lookupResponse]
    );

    const { useFetch } = useAPIQuery({
        key: "api/onsite-issues/machines",
        endpoint: "/api/onsite-issues/machines/",
    });

    const { data, isLoading, isFetching } = useFetch({
        params: {
            page_no: pageIndex + 1,
            entries,
            q: globalFilterValue || undefined,
            department_id: departmentId || undefined,
            sort_q: sorting.map((item) => (item?.desc ? "desc" : "asc")).join(",") || undefined,
            sort_c: sorting.map((item) => item?.id).join(",") || undefined,
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 30,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });

    const rows = Array.isArray(data?.results?.data)
        ? data.results.data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];

    const handleGlobalFilterChange = (value) => {
        const q = value?.trim() || "";
        setGlobalFilterValue(q);
        setPageIndex(0);
        setSearchParams((params) => {
            if (q) params.set("q", q);
            else params.delete("q");
            params.set("page", "1");
            return params;
        });
    };

    const handleDepartmentChange = (value) => {
        setDepartmentId(value);
        setPageIndex(0);
        setSearchParams((params) => {
            if (value) params.set("department_id", value);
            else params.delete("department_id");
            params.set("page", "1");
            return params;
        });
    };

    const handlePagination = (value) => {
        setSearchParams((params) => {
            params.set("entries", value.pageSize);
            params.set("page", value.pageIndex + 1);
            return params;
        });
        setPageIndex(value.pageIndex);
        setEntries(value.pageSize);
    };

    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content">
                        <h3 className="nk-block-title page-title">
                            On-site Issues
                        </h3>
                        <p className="text-soft">
                            Select a machine to view and log on-site problems.
                            Pending issues are listed first.
                        </p>
                    </div>
                    <div className="nk-block-head-content">
                        <div className="form-group" style={{ minWidth: 220 }}>
                            <label className="form-label">Department</label>
                            <select
                                className="form-control"
                                value={departmentId}
                                onChange={(event) =>
                                    handleDepartmentChange(event.target.value)
                                }
                            >
                                <option value="">All departments</option>
                                {departments.map((row) => (
                                    <option key={row.id} value={row.id}>
                                        {row.department_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>
            <Table
                data={rows}
                columns={columns}
                pagination
                pageIndex={pageIndex}
                pageSize={entries}
                rowCount={typeof data?.count === "number" ? data.count : rows.length}
                globalSearch
                isLoading={isLoading || isFetching}
                editRows={false}
                deleteRows={false}
                filterModal={false}
                selectRows={false}
                defaultGlobalSearchValue={globalFilterValue || undefined}
                onGlobalFilterChange={handleGlobalFilterChange}
                onPaginationChange={handlePagination}
                onSortingChange={setSorting}
                onRowEdit={(row) =>
                    navigate("/onsite-issue-list", {
                        state: {
                            machineId: row.original.id,
                            machineName: row.original.machine_name,
                        },
                    })
                }
            />
        </>
    );
};

export default OnsiteIssueMachines;
